/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { upperCase } from "lodash";
import { AuthenticationService } from "../../../core/authentication/authentication.service";
import { LoadingService } from "../../../core/loader/loading.service";
import { DataTableConfig } from "../../../shared/components/data-table/data-table-service.type";
import { DataTableComponent } from "../../../shared/components/data-table/data-table.component";
import {
  DataTable,
  DataTableColumn,
  GetDataEvent,
  UpdateDataEvent,
} from "../../../shared/components/data-table/data-table.type";
import { DeleteModalComponent } from "../../../shared/components/delete-modal/delete-modal.component";
import { QuickViewModalComponent } from "../../../shared/components/quick-view-modal/quick-view-modal.component";
import { fileIcons } from "../../../shared/constants/icons";
import { UserInfo, UserTypes } from "../../../shared/types/user-info";
import { CompanyService } from "../companies/company.service";
import { getContractAttachmentSearchFilterOptions } from "./company-attachment";
import { CompanyAttachmentService } from "./company-attachment.service";
import { CompanyAttachment } from "./company-attachment.type";

@Injectable({
  providedIn: "root",
})
export class CompanyAttachmentTableService {
  currentUser: UserInfo;

  constructor(
    public companyAttachmentService: CompanyAttachmentService,
    private dialog: MatDialog,
    private loader: LoadingService,
    private authenticationService: AuthenticationService,
    private companyService: CompanyService,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
  }

  getCompanyAttachmentsTableConfig = (config?: DataTableConfig): DataTable<CompanyAttachment> => {
    const { hidden, shown, actionColumn } = config || {};

    const columns: DataTableColumn<CompanyAttachment>[] = (
      [
        {
          prop: "extension",
          name: "File Type",
          icon: {
            class: this.getIconClass,
            tooltip: this.getTooltipValue,
          },
        },
        {
          prop: "name",
          name: "Name",
          link: {
            target: "_blank",
            path: (companyAttachment) => companyAttachment.url,
            useHref: true,
          },
        },
        {
          prop: "description",
          name: "Description",
          inlineEdit: {
            control: "input",
            disabled: (companyAttachment) => !this.canEditOrDeleteCompanyAttachment(companyAttachment),
          },
        },
        {
          prop: "company.name",
          name: "Company",
          hidden: this.currentUser.isCompanyOrViewAsCompany({ ignoreIfMultiCompanies: true }),
          link: {
            path: (contract) => `/companies/${contract.companyId}`,
            disabled: (contract) => this.currentUser.isCompanyOrViewAsCompany(),
          },
        },
      ] as DataTableColumn<CompanyAttachment>[]
    )
      .filter((col) => (shown ? shown.includes(col.prop) : true) && (hidden ? !hidden.includes(col.prop) : true))
      .map((col) => ({
        ...col,
        inlineEdit: {
          ...col?.inlineEdit,
          /** Use the set condition and the new one passed here */
          disabled: (companyAttachment) => col?.inlineEdit?.disabled?.(companyAttachment),
        },
      }));

    return {
      id: "companyAttachment",
      entityName: "CompanyAttachment",
      syncWithQueryParams: true,
      columns,
      defaultColumns: [],
      actionColumn: actionColumn?.hidden
        ? null
        : [
            {
              icon: "zoom_in",
              text: "Quick View",
              function: this.viewCompanyAttachment,
            },
            {
              icon: "clear",
              text: "Delete",
              function: this.deleteCompanyAttachment,
              hidden: (companyAttachment) => !this.canEditOrDeleteCompanyAttachment(companyAttachment),
            },
          ],
      searchBar: [
        {
          availableSearchFields: getContractAttachmentSearchFilterOptions(this.currentUser, { hidden, shown }),
        },
      ],
    };
  };

  /** Populate company attachments table */
  getCompanyAttachments = (event: GetDataEvent<CompanyAttachment>): void => {
    const { onError, onResult, queryParams } = event;
    this.companyAttachmentService
      .getCompanyAttachments(this.currentUser.selectedCompanyId || this.currentUser.companyId, {
        relations: { company: true },
        ...queryParams,
        where: {
          ...queryParams?.where,
        },
      })
      .subscribe(
        (data): void => {
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };

  update = (event: UpdateDataEvent<CompanyAttachment>): void => {
    const { onError, onResult, row, updatedValues } = event;
    if (row.id && updatedValues) {
      this.loader.show();
      this.companyService.updateAttachment(row.companyId, row.id, { ...updatedValues }).subscribe(
        (data): void => {
          onResult(data);
          this.loader.hide();
        },
        (error): void => {
          onError(error);
          this.loader.hide();
        },
      );
    } else {
      onError();
    }
  };

  canEditOrDeleteCompanyAttachment = (companyAttachment: CompanyAttachment): boolean => {
    return !(
      this.currentUser.hasRight(["viewer"]) ||
      this.currentUser.isCompanyOrViewAsCompany() ||
      ((this.currentUser.isPartnerOrViewAsPartner() || this.currentUser.isCompanyOrViewAsCompany()) &&
        companyAttachment.uploadedByType === UserTypes.Global)
    );
  };

  canAddOrUpdateAttachment(): boolean {
    return !(this.currentUser.isCompanyOrViewAsCompany() || this.currentUser.hasRight(["viewer"]));
  }

  // #region Private methods
  private viewCompanyAttachment = (companyAttachment: CompanyAttachment): void => {
    const data = [
      { name: "Name", value: companyAttachment.name },
      { name: "Description", value: companyAttachment.description },
    ];
    this.dialog.open(QuickViewModalComponent, {
      width: "500px",
      data: {
        title: "Quick-view",
        data: data,
      },
      panelClass: "quick-view-modal",
    });
  };

  private deleteCompanyAttachment = (
    companyAttachment: CompanyAttachment,
    table: DataTableComponent<CompanyAttachment>,
  ): void => {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "companyAttachment",
        data: companyAttachment,
        url: `${this.companyService.basePath}/${companyAttachment.companyId}/company-attachments`,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        table.search({ isDeletingOrUnlinking: true });
      }
    });
  };

  private getTooltipValue = (companyAttachment: CompanyAttachment): string => upperCase(companyAttachment.extension);

  private getIconClass = (companyAttachment: CompanyAttachment): string =>
    `fa ${fileIcons[companyAttachment.extension] || fileIcons.default}`;

  // #endregion
}
